import { Link } from "react-router-dom";
import { useState } from "react";
import { Search, User, Heart, ShoppingCart, Menu, X, LogOut } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

interface SubMenuItem {
  name: string;
  href: string;
}

interface NavLink {
  name: string;
  href: string;
  icon: string;
  submenu?: SubMenuItem[];
}

const navLinks: NavLink[] = [
  { name: "Home", href: "/", icon: "home" },
  { name: "Programs", href: "/programs", icon: "programs" },
  { name: "Trainers", href: "/trainer-discovery", icon: "trainers" },
  { name: "Nutrition", href: "/nutritionist-discovery", icon: "nutrition" },
  { name: "Park Classes", href: "/park-classes", icon: "park-classes" },
  { name: "Pricing", href: "/pricing", icon: "pricing" },
  { name: "About", href: "/about", icon: "info" },
  { name: "FAQ", href: "/faq", icon: "faq" },
  { name: "Contact", href: "/contact", icon: "headset" },
];

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isEventsOpen, setIsEventsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { profile, signOut } = useAuth();

  return (
    <header id="main-header" role="banner" className="sticky top-0 z-50 bg-white shadow-md">
      <div className="akash-header">
        {/* Marquee Section */}
        <div className="header-marq bg-blue-600 text-white py-2 overflow-hidden relative" aria-label="Announcements">
          <div className="flex animate-marquee whitespace-nowrap" aria-hidden="true">
            <span className="mx-8">
              <Link to="/programs" className="hover:underline focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-600 rounded">
                Transform Your Fitness Journey Today
              </Link>
            </span>
            <span className="mx-8">
              <Link to="/" className="hover:underline focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-600 rounded">
                New Programs Available
              </Link>
            </span>
            <span className="mx-8">
              <Link to="/programs" className="hover:underline focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-600 rounded">
                Transform Your Fitness Journey Today
              </Link>
            </span>
            <span className="mx-8">
              <Link to="/" className="hover:underline focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-600 rounded">
                New Programs Available
              </Link>
            </span>
          </div>
        </div>

        {/* Top Header Section */}
        <div className="header-top-container bg-white border-b border-gray-200">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between py-4 gap-4">
              {/* Logo */}
              <div className="logo-container flex-shrink-0">
                <Link 
                  to="/" 
                  className="flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 rounded"
                  aria-label="FitEasy Home"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-800 rounded-full flex items-center justify-center text-white font-bold text-xl" aria-hidden="true">
                    FE
                  </div>
                  <span className="text-xl font-bold text-gray-900 hidden sm:block">FitEasy</span>
                </Link>
              </div>

              {/* Center: Heading and Search */}
              <div className="search-and-button-container flex-1 flex flex-col items-center gap-3 max-w-2xl mx-4">
                <h2 className="text-lg font-semibold text-blue-600 text-center sr-only sm:not-sr-only">
                  Your Fitness Journey Starts Here
                </h2>
                <div className="input-container relative w-full">
                  <label htmlFor="search-input" className="sr-only">
                    Search programs, workouts, and nutrition
                  </label>
                  <input
                    type="search"
                    id="search-input"
                    name="search"
                    placeholder="Search programs, workouts, nutrition..."
                    autoComplete="off"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    aria-label="Search programs, workouts, and nutrition"
                  />
                  <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" aria-hidden="true" />
                </div>
              </div>

              {/* Right: Profile, Wishlist, Cart */}
              <div className="header-cart-btn-conatiner flex items-center gap-3 flex-shrink-0 relative">
                {profile ? (
                  <div className="profile-section hidden md:flex items-center gap-2 px-3 py-1 rounded-full hover:bg-gray-100 transition group">
                    <span className="user-name-display text-sm text-gray-700">
                      {profile.full_name || 'User'}
                    </span>
                    <button
                      id="profile-wrapper"
                      className="p-2 rounded-full hover:bg-gray-200 transition focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
                      title="Profile"
                      aria-label="User profile menu"
                      aria-expanded={false}
                      aria-haspopup="true"
                    >
                      <User className="h-5 w-5 text-gray-700" aria-hidden="true" />
                    </button>
                    <ul 
                      className="home-profile-list-container absolute top-full right-0 mt-2 bg-white shadow-lg rounded-lg p-2 min-w-[180px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200"
                      role="menu"
                      aria-label="User menu"
                    >
                      <li className="px-3 py-2 text-sm font-semibold border-b" role="none">
                        <span>Hey, {profile.full_name || 'User'}</span>
                      </li>
                      <li role="menuitem">
                        <Link
                          to="/profile"
                          className="block px-3 py-2 text-sm hover:bg-gray-100 rounded focus:outline-none focus:ring-2 focus:ring-blue-600"
                        >
                          My Profile
                        </Link>
                      </li>
                      <li role="menuitem">
                        <Link
                          to="/dashboard"
                          className="block px-3 py-2 text-sm hover:bg-gray-100 rounded focus:outline-none focus:ring-2 focus:ring-blue-600"
                        >
                          Dashboard
                        </Link>
                      </li>
                      <li role="menuitem">
                        <Link
                          to="/bookings"
                          className="block px-3 py-2 text-sm hover:bg-gray-100 rounded focus:outline-none focus:ring-2 focus:ring-blue-600"
                        >
                          My Bookings
                        </Link>
                      </li>
                      <li role="menuitem" className="border-t mt-1 pt-1">
                        <button
                          onClick={signOut}
                          className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded focus:outline-none focus:ring-2 focus:ring-red-600"
                        >
                          <LogOut className="h-4 w-4" />
                          Sign Out
                        </button>
                      </li>
                    </ul>
                  </div>
                ) : (
                  <Link
                    to="/login"
                    className="hidden md:flex items-center gap-2 px-4 py-2 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
                  >
                    <User className="h-4 w-4" />
                    <span>Login</span>
                  </Link>
                )}
                <button
                  className="mob-d-none hidden md:flex p-2 rounded-full hover:bg-gray-100 transition focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
                  title="Wishlist"
                  aria-label="View wishlist"
                >
                  <Heart className="h-5 w-5 text-gray-700" aria-hidden="true" />
                </button>
                <Link 
                  to="/cart" 
                  className="mob-d-none hidden md:flex p-2 rounded-full hover:bg-gray-100 transition relative focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
                  aria-label="View shopping cart"
                >
                  <ShoppingCart className="h-5 w-5 text-gray-700" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Section */}
        <nav className="header-container bg-blue-600" role="navigation" aria-label="Main navigation">
          <div className="container mx-auto px-4">
            <div className="navbar-container flex items-center justify-between">
              {/* Hamburger Menu (Mobile) */}
              <button
                className="hamburger-menu md:hidden p-2 text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-600 rounded"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label="Toggle menu"
                aria-expanded={isMenuOpen}
                aria-controls="main-navigation"
              >
                {isMenuOpen ? (
                  <X className="h-6 w-6" aria-hidden="true" />
                ) : (
                  <Menu className="h-6 w-6" aria-hidden="true" />
                )}
              </button>

              {/* Mobile Cart Buttons */}
              <div className="mobile-cart-buttons flex md:hidden items-center gap-2">
                {profile ? (
                  <Link
                    to="/profile"
                    className="mobile-profile-btn p-2 rounded-full bg-white/20 hover:bg-white/30 transition focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-600"
                    title="Profile"
                    aria-label="Go to profile"
                  >
                    <User className="h-5 w-5 text-white" />
                  </Link>
                ) : (
                  <Link
                    to="/login"
                    className="mobile-profile-btn p-2 rounded-full bg-white/20 hover:bg-white/30 transition focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-600"
                    title="Profile"
                    aria-label="Go to login"
                  >
                    <User className="h-5 w-5 text-white" />
                  </Link>
                )}
                <button
                  className="mobile-heart-btn p-2 rounded-full bg-white/20 hover:bg-white/30 transition"
                  title="Favorites"
                >
                  <Heart className="h-5 w-5 text-white" />
                </button>
                <button
                  className="mobile-cart-btn p-2 rounded-full bg-white/20 hover:bg-white/30 transition"
                  title="My Programs"
                >
                  <Link to="/cart" className="text-white">
                    <ShoppingCart className="h-5 w-5" />
                  </Link>
                </button>
              </div>

              {/* Navigation Menu */}
              <ul
                id="main-navigation"
                className={`nav-menu-list flex flex-col md:flex-row items-start md:items-center gap-0 md:gap-1 ${
                  isMenuOpen ? "block" : "hidden md:flex"
                } absolute md:relative top-full left-0 right-0 md:top-auto bg-blue-600 md:bg-transparent w-full md:w-auto`}
                role="menubar"
              >
                {navLinks.map((link) => (
                  <li
                    key={link.name}
                    className="relative w-full md:w-auto group"
                    role="none"
                    onMouseEnter={() =>
                      link.submenu && setIsEventsOpen(true)
                    }
                    onMouseLeave={() =>
                      link.submenu && setIsEventsOpen(false)
                    }
                  >
                    <Link
                      to={link.href}
                      className="flex items-center gap-2 px-4 py-3 md:py-4 text-white hover:bg-white/20 transition w-full md:w-auto focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-600 rounded"
                      onClick={() => link.submenu && setIsEventsOpen(!isEventsOpen)}
                      role="menuitem"
                      aria-haspopup={link.submenu ? "true" : undefined}
                      aria-expanded={link.submenu && isEventsOpen ? "true" : undefined}
                    >
                      <span>{link.name}</span>
                      {link.submenu && (
                        <span className="toggle ml-auto md:ml-2" aria-hidden="true">▼</span>
                      )}
                    </Link>
                    {link.submenu && (
                      <ul
                        className={`dropdown cus-dropdown ${
                          isEventsOpen ? "block" : "hidden md:block md:opacity-0 md:invisible"
                        } group-hover:opacity-100 group-hover:visible absolute top-full left-0 bg-white shadow-lg rounded-lg min-w-[150px] z-10 transition-all duration-200`}
                        role="menu"
                        aria-label={`${link.name} submenu`}
                      >
                        {link.submenu.map((subItem) => (
                          <li key={subItem.name} role="none">
                            <Link
                              to={subItem.href}
                              className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded focus:outline-none focus:ring-2 focus:ring-blue-600"
                              onClick={() => {
                                setIsMenuOpen(false);
                                setIsEventsOpen(false);
                              }}
                              role="menuitem"
                            >
                              {subItem.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </nav>
      </div>
    </header>
  );
}

